import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { X, Copy, Share2, Mail, Loader2, Download } from "lucide-react";
import { useState, useEffect } from "react";
import { SiWhatsapp, SiFacebook, SiInstagram, SiPinterest, SiX } from "react-icons/si";
import { useI18n } from "@/lib/i18n";
import { getToken } from "@/lib/authContext";

interface ShareModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    text: string;
    url: string;
    imageUrl?: string;
    entityType?: 'shop' | 'route' | 'post' | 'restyle' | 'spaceblend';
    entityId?: string;
}

export function ShareModal({ open, onOpenChange, title, text, url, imageUrl: initialImageUrl, entityType, entityId }: ShareModalProps) {
    const { t } = useI18n();
    const { toast } = useToast();
    const [copied, setCopied] = useState(false);
    const [sharing, setSharing] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [serverImageUrl, setServerImageUrl] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [hasFailed, setHasFailed] = useState(false);
    const toAbsoluteUrl = (value?: string | null): string | null => {
        if (!value) return null;
        try {
            return new URL(value, window.location.origin).toString();
        } catch {
            return value;
        }
    };

    const openExternalShare = (targetUrl: string): boolean => {
        try {
            const opened = window.open(targetUrl, "_blank", "noopener,noreferrer");
            if (opened) return true;

            const anchor = document.createElement("a");
            anchor.href = targetUrl;
            anchor.target = "_blank";
            anchor.rel = "noopener noreferrer";
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            return true;
        } catch (err) {
            console.error("Failed to open external share url:", err);
            return false;
        }
    };

    const fetchShareFile = async (): Promise<File | null> => {
        if (!activeImageUrl || imageError) return null;
        try {
            const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(activeImageUrl)}`;
            const response = await fetch(proxyUrl);
            if (!response.ok) return null;
            const blob = await response.blob();
            if (!blob.type.startsWith("image/")) return null;
            return new File([blob], "vintmap-share.png", { type: blob.type });
        } catch (err) {
            console.warn("Failed to fetch share image file:", err);
            return null;
        }
    };

    // Use server image if available, else initial
    const activeImageUrl = serverImageUrl || initialImageUrl;
    const hasInitialImage = !!initialImageUrl;
    const absoluteImageUrl = toAbsoluteUrl(activeImageUrl);

    // Reset image error when imageUrl changes or modal opens
    if (!open && imageError) setImageError(false);

    // Reset generated URL when entity changes
    useEffect(() => {
        setServerImageUrl(null);
        setHasFailed(false);
    }, [entityType, entityId]);

    // Fetch official branded image only when no branded image is available.
    useEffect(() => {
        // We want to fetch a branded image IF:
        // 1. Modal is open
        // 2. We have entityType and entityId
        // 3. We haven't fetched one yet (serverImageUrl is null)
        // 4. We aren't currently fetching (isGenerating is false)
        // 5. AND (the initial image is missing OR it's not already a branded/internal URL)
        // 6. AND it's not a post (user feedback: branding not desired for gems)
        // 7. AND it's not a restyle (composite is already branded with overlay + URL)

        const isAlreadyBranded = initialImageUrl?.includes('share-images') ||
            initialImageUrl?.includes('vintmap-share') ||
            initialImageUrl?.includes('maps.googleapis.com'); // Maps are considered "pre-branded" enough for quick share

        if (open && entityType && entityId && !serverImageUrl && !isGenerating && !hasFailed && (!hasInitialImage || !isAlreadyBranded)) {
            const startGeneration = async () => {
                setIsGenerating(true);
                try {
                    const token = await getToken();
                    const res = await fetch('/api/generate-share-image', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ type: entityType, id: entityId }),
                    });

                    if (!res.ok) {
                        const err = await res.json().catch(() => ({ error: "Server error" }));
                        throw new Error(err.error || "Generation failed");
                    }

                    const data = await res.json();
                    if (data.url) {
                        setServerImageUrl(data.url);
                        setImageError(false);
                    }
                } catch (err) {
                    console.error("Failed to generate share image", err);
                    setHasFailed(true);
                } finally {
                    setIsGenerating(false);
                }
            };

            startGeneration();
        }
    }, [open, entityType, entityId, hasInitialImage, initialImageUrl, serverImageUrl, isGenerating, hasFailed]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(`${title}\n${text}\n\n${url}`);
            setCopied(true);
            toast({
                title: t("share.copiedTitle"),
                description: t("share.copiedDesc"),
                duration: 2000,
            });
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy", err);
        }
    };

    const shareNative = async () => {
        setSharing(true);
        try {
            if ('share' in navigator) {
                const fileObj = await fetchShareFile();

                // Stage 1: Attempt share WITH file
                if (fileObj && navigator.canShare && navigator.canShare({ files: [fileObj] })) {
                    try {
                        console.log("Attempting share with file...");
                        await navigator.share({
                            files: [fileObj],
                            title,
                            text: `${title}\n\n${text}\n\n${url}`, // Some apps drop URL if file is present
                        });
                        return; // Success!
                    } catch (err) {
                        console.warn("Share with file failed, retrying without file...", err);
                    }
                }

                // Stage 2: Fallback (Text/URL only)
                console.log("Sharing text/url only...");
                await navigator.share({
                    title,
                    text: `${title}\n\n${text}`, // URL shouldn't be here, it is passed separately
                    url
                });

            } else {
                toast({
                    title: t("share.notSupportedTitle"),
                    description: t("share.notSupportedDesc"),
                    variant: "destructive"
                });
            }
        } catch (err) {
            console.error("Share failed completely:", err);
            if (err instanceof Error && err.name !== 'AbortError') {
                toast({
                    title: t("share.failedTitle"),
                    description: t("share.failedDesc"),
                    variant: "destructive"
                });
            }
        } finally {
            setSharing(false);
        }
    };

    const shareInstagram = async () => {
        // Keep icon behavior consistent: direct action per channel.
        // Instagram web does not support direct prefilled share with media, so we download.
        if (activeImageUrl) {
            try {
                toast({ title: t("share.pleaseWait"), description: t("share.downloadingImage") });
                const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(activeImageUrl)}`;
                const response = await fetch(proxyUrl);
                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = "vintmap-share.png";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);

                toast({
                    title: t("share.imageDownloadedTitle"),
                    description: t("share.imageDownloadedDesc"),
                    duration: 5000
                });
            } catch (e) {
                console.error(e);
                const fallbackUrl = absoluteImageUrl || activeImageUrl;
                const didOpen = openExternalShare(fallbackUrl);
                if (!didOpen) {
                    toast({
                        title: t("share.failedTitle"),
                        description: t("share.failedDesc"),
                        variant: "destructive"
                    });
                }
            }
        } else {
            toast({
                title: t("share.noImageTitle"),
                description: t("share.noImageDesc"),
                variant: "destructive"
            });
        }
    };

    const shareWhatsApp = () => {
        const encodedText = encodeURIComponent(
            `*${title}*\n\n${text}\n\n${url}${absoluteImageUrl ? `\n\n${absoluteImageUrl}` : ""}`
        );
        const didOpen = openExternalShare(`https://api.whatsapp.com/send?text=${encodedText}`);
        if (!didOpen) {
            toast({
                title: t("share.failedTitle"),
                description: t("share.failedDesc"),
                variant: "destructive"
            });
        }
    };

    const shareEmail = () => {
        const subject = encodeURIComponent(title);
        let bodyText = `${text}\n\nLink: ${url}`;
        if (activeImageUrl) {
            // mailto doesn't support file attachments, so we append the image URL
            bodyText += `\n\nAfbeelding: ${absoluteImageUrl || activeImageUrl}`;
        }
        const body = encodeURIComponent(bodyText);
        const didOpen = openExternalShare(`mailto:?subject=${subject}&body=${body}`);
        if (!didOpen) {
            toast({
                title: t("share.failedTitle"),
                description: t("share.failedDesc"),
                variant: "destructive"
            });
        }
    };

    const shareFacebook = () => {
        const encodedUrl = encodeURIComponent(url);
        const encodedQuote = encodeURIComponent(`${title}\n${text}${absoluteImageUrl ? `\n${absoluteImageUrl}` : ""}`);
        const didOpen = openExternalShare(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedQuote}`);
        if (!didOpen) {
            toast({
                title: t("share.failedTitle"),
                description: t("share.failedDesc"),
                variant: "destructive"
            });
        }
    };

    const sharePinterest = () => {
        // Pinterest needs URL (link origin), Media (Image), and Description
        const encodedUrl = encodeURIComponent(url);
        const encodedMedia = absoluteImageUrl ? encodeURIComponent(absoluteImageUrl) : "";
        const encodedDesc = encodeURIComponent(`${title}: ${text}`);
        const didOpen = openExternalShare(`https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedMedia}&description=${encodedDesc}`);
        if (!didOpen) {
            toast({
                title: t("share.failedTitle"),
                description: t("share.failedDesc"),
                variant: "destructive"
            });
        }
    };

    const shareX = () => {
        const encodedText = encodeURIComponent(`${title}\n${url}${absoluteImageUrl ? `\n${absoluteImageUrl}` : ""}`);
        const didOpen = openExternalShare(`https://twitter.com/intent/tweet?text=${encodedText}`);
        if (!didOpen) {
            toast({
                title: t("share.failedTitle"),
                description: t("share.failedDesc"),
                variant: "destructive"
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-xl max-w-[95vw] rounded-2xl bg-white p-0 overflow-hidden">
                {/* Header */}
                <DialogHeader className="px-5 pt-5 pb-3 border-b">
                    <DialogTitle className="text-base font-bold">
                        {t("share.titlePrefix")} {
                            entityType === 'shop' ? t("shops.title") :
                                entityType === 'route' ? t("nav.routes") :
                                    entityType === 'post' ? t("community.userGems") || "Parel" :
                                        entityType === 'restyle' ? t("nav.restyle") || "Restyle" :
                                            entityType === 'spaceblend' ? t("spaceblend.title") || "SpaceBlend" : ""
                        }
                    </DialogTitle>
                    <DialogDescription className="sr-only">{t("share.desc")}</DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-0">
                    {/* Image — takes up max space, 1:1 square */}
                    <div className="relative w-full aspect-square bg-slate-50 flex items-center justify-center">
                        {isGenerating && !activeImageUrl && (
                            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-slate-800 bg-white/80">
                                <Loader2 className="h-8 w-8 animate-spin mb-2 text-slate-600" />
                                <span className="text-[10px] uppercase font-bold tracking-widest text-slate-600">{t("share.applyingBranding")}</span>
                            </div>
                        )}
                        {isGenerating && activeImageUrl && (
                            <div className="absolute top-3 left-3 z-20 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-full">
                                <Loader2 className="h-3 w-3 animate-spin text-white/80" />
                                <span className="text-[8px] uppercase font-bold tracking-tighter text-white/80">{t("share.applyingBranding")}</span>
                            </div>
                        )}

                        {(activeImageUrl && !imageError) ? (
                            <img
                                src={activeImageUrl.includes('maps.googleapis.com')
                                    ? `/api/proxy-image?url=${encodeURIComponent(activeImageUrl)}`
                                    : activeImageUrl}
                                alt="Share Preview"
                                className="w-full h-full object-contain m-auto"
                                onError={() => setImageError(true)}
                            />
                        ) : (
                            <div className="flex flex-col items-center justify-center p-8 text-center w-full h-full bg-slate-900 text-white">
                                <div className="text-3xl font-bold mb-1 tracking-tighter text-[#F90F5A] drop-shadow-md">{t("app.name")}</div>
                            </div>
                        )}
                    </div>

                    {/* Share buttons */}
                    <div className="px-5 py-4 flex flex-col gap-3">
                        {/* Social grid */}
                        <div className="grid grid-cols-5 gap-2">
                            <Button variant="outline" className="flex flex-col h-14 gap-1 hover:bg-green-50 hover:border-green-200" onClick={shareWhatsApp}>
                                <SiWhatsapp className="h-5 w-5 text-green-500" />
                                <span className="text-[9px] font-medium text-muted-foreground">{"WhatsApp"}</span>
                            </Button>
                            <Button variant="outline" className="flex flex-col h-14 gap-1 hover:bg-blue-50 hover:border-blue-200" onClick={shareFacebook}>
                                <SiFacebook className="h-5 w-5 text-blue-600" />
                                <span className="text-[9px] font-medium text-muted-foreground">{"Facebook"}</span>
                            </Button>
                            <Button variant="outline" className="flex flex-col h-14 gap-1 hover:bg-red-50 hover:border-red-200" onClick={sharePinterest}>
                                <SiPinterest className="h-5 w-5 text-red-600" />
                                <span className="text-[9px] font-medium text-muted-foreground">{"Pinterest"}</span>
                            </Button>
                            <Button variant="outline" className="flex flex-col h-14 gap-1 hover:bg-pink-50 hover:border-pink-200" onClick={shareInstagram}>
                                <SiInstagram className="h-5 w-5 text-pink-600" />
                                <span className="text-[9px] font-medium text-muted-foreground">{"Instagram"}</span>
                            </Button>
                            <Button variant="outline" className="flex flex-col h-14 gap-1 hover:bg-orange-50 hover:border-orange-200" onClick={shareEmail}>
                                <Mail className="h-5 w-5 text-orange-500" />
                                <span className="text-[9px] font-medium text-muted-foreground">{t("share.email")}</span>
                            </Button>
                        </div>

                        {/* Bottom row: Copy link + Native share */}
                        <div className="flex items-center gap-2">
                            <Input value={url} readOnly className="h-9 text-xs bg-muted/50 flex-1" />
                            <Button size="sm" variant="outline" className="h-9 min-w-[3rem]" onClick={handleCopy}>
                                {copied ? "👍" : <Copy className="h-4 w-4" />}
                            </Button>
                            {'share' in navigator && (
                                <Button onClick={shareNative} className="h-9 bg-primary text-primary-foreground font-semibold px-4" disabled={sharing}>
                                    {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4 mr-1" />}
                                    {sharing ? "..." : t("share.shareVia")}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
